import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicTable from "../../../../components/lab-dynamic-table";

const Employee = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Helper function to format collection centers display
  const formatCollectionCenters = (collectionCenter: any) => {
    if (!collectionCenter) return "N/A";

    if (Array.isArray(collectionCenter)) {
      if (collectionCenter.length === 0) return "No centers assigned";
      return collectionCenter
        .map((center: any) => center.collectionCenterName)
        .join(", ");
    }

    return collectionCenter || "N/A";
  };

  // Category related states
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Collection Center filter states
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("");
  const [collectionCentersForFilter, setCollectionCentersForFilter] = useState<
    any[]
  >([]);

  // Collection Center allocation states
  const [collectionCenters, setCollectionCenters] = useState<any[]>([]);
  const [openAllocationDialog, setOpenAllocationDialog] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedCenterIds, setSelectedCenterIds] = useState<string[]>([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [allocatingCenters, setAllocatingCenters] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  // Frontend search functionality - filter employees by name
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const searchTerm = debouncedSearch.toLowerCase().trim();
    const filtered = employees.filter((employee: any) => {
      const firstName = (employee?.firstName || "").toLowerCase();
      const fullName = (employee?.fullName || "").toLowerCase();
      const displayName = firstName || fullName;

      return displayName.includes(searchTerm);
    });

    setFilteredEmployees(filtered);

    // Reset to first page when search changes
    if (page > 0 && filtered.length <= page * rowsPerPage) {
      setPage(0);
    }
  }, [employees, debouncedSearch, page, rowsPerPage]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/user-category/getAllCategories`,
        {
          params: {
            pageNo: 1,
            count: 1000,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setCategories(response.data?.data || []);
      } else {
        toast.error(response.data?.message || "Failed to fetch categories");
      }
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      toast.error(
        error?.message || "Error fetching categories"
      );
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch collection centers from API
  const fetchCollectionCenters = async () => {
    try {
      setLoadingCenters(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/collection-center/getAllByPagination`,
        {
          params: {
            pageNo: 1,
            count: 1000,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setCollectionCenters(response.data?.data || []);
      } else {
        toast.error(
          response.data?.message || "Failed to fetch collection centers"
        );
      }
    } catch (error: any) {
      console.error("Error fetching collection centers:", error);
      toast.error(
        error?.message || "Error fetching collection centers"
      );
    } finally {
      setLoadingCenters(false);
    }
  };

  // Fetch collection centers for filter dropdown
  const fetchCollectionCentersForFilter = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/collection-center/getAllByPagination`,
        {
          params: {
            pageNo: 1,
            count: 1000,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setCollectionCentersForFilter(response.data?.data || []);
      } else {
        console.error("Failed to fetch collection centers for filter");
      }
    } catch (error: any) {
      console.error("Error fetching collection centers for filter:", error);
    }
  };

  // API call to fetch hospital users department wise
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageNo: page + 1,
        count: rowsPerPage,
        // Removed search from API params - now handling search on frontend
      };

      // Add category filter if a category is selected
      if (selectedCategory && selectedCategory._id) {
        params.categoryId = selectedCategory._id;
      }

      // Add collection center filter if selected
      if (selectedCollectionCenterId) {
        params.collectionCenterId = selectedCollectionCenterId;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getHospitalAllUsersDepartmentWise`,
        {
          params,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setEmployees(response.data?.data || []);
        setFilteredEmployees(response.data?.data || []);
        setTotal(response.data?.totalCount || 0);
      } else {
        toast.error(response.data?.message || "Failed to fetch employees");
      }
    } catch (error: any) {
      console.error("Error fetching employees:", error);
      toast.error(error?.response.data.message || "Error fetching employees");
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  // Handle category selection
  const handleCategoryChange = (event: any, newValue: any) => {
    setSelectedCategory(newValue);
    setPage(0); // Reset to first page when category changes
  };

  // Handle collection center filter change
  const handleCollectionCenterFilterChange = (event: any) => {
    setSelectedCollectionCenterId(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  // Get paginated data from filtered results
  const getPaginatedData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredEmployees.slice(startIndex, endIndex);
  };

  // Handle allocation dialog open
  const handleAllocationClick = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);

    // Pre-select already allocated centers - check in filtered employees
    const employee = filteredEmployees.find(
      emp => (emp._id || emp.id) === employeeId
    );
    const currentCenterIds =
      employee?.collectionCenter && Array.isArray(employee.collectionCenter)
        ? employee.collectionCenter.map((center: any) => center._id)
        : [];

    setSelectedCenterIds(currentCenterIds);
    setOpenAllocationDialog(true);
    fetchCollectionCenters();
  };

  // Handle collection center selection
  const handleCenterSelection = (centerId: string, checked: boolean) => {
    if (checked) {
      setSelectedCenterIds(prev => [...prev, centerId]);
    } else {
      setSelectedCenterIds(prev => prev.filter(id => id !== centerId));
    }
  };

  // Handle allocation API call
  const handleAllocateCenters = async () => {
    try {
      setAllocatingCenters(true);
      const response = await axios.put(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/allocateCollectionCenterToUsers/${selectedEmployeeId}`,
        {
          collectionCenterIds: selectedCenterIds,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        toast.success("Collection centers allocated successfully");
        setOpenAllocationDialog(false);
        fetchEmployees(); // Refresh the employee list
      } else {
        toast.error(
          response.data?.message || "Failed to allocate collection centers"
        );
      }
    } catch (error: any) {
      console.error("Error allocating collection centers:", error);
      toast.error(
        error?.response?.data?.message || "Error allocating collection centers"
      );
    } finally {
      setAllocatingCenters(false);
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenAllocationDialog(false);
    setSelectedEmployeeId("");
    setSelectedCenterIds([]);
  };

  // Fetch categories and collection centers on component mount
  useEffect(() => {
    fetchCategories();
    fetchCollectionCentersForFilter();
  }, []);

  // Fetch employees on component mount and when dependencies change
  useEffect(() => {
    fetchEmployees();
  }, [page, rowsPerPage, selectedCategory, selectedCollectionCenterId]); // Added selectedCollectionCenterId

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1rem",
              sm: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Employees
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ minWidth: 200 }}>
            <Autocomplete
              size="small"
              options={categories}
              getOptionLabel={(option: any) => option?.categoryName || ""}
              value={selectedCategory}
              onChange={handleCategoryChange}
              isOptionEqualToValue={(option: any, value: any) =>
                option?._id === value?._id
              }
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  size="small"
                  placeholder="Filter by Category"
                  label="Category"
                />
              )}
              disabled={loadingCategories}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel sx={{ top: -4 }} id="collection-center-filter-label">
              Collection Center
            </InputLabel>
            <Select
              labelId="collection-center-filter-label"
              id="collection-center-filter"
              value={selectedCollectionCenterId}
              label="Collection Center"
              onChange={handleCollectionCenterFilterChange}
              className="custom-input"
            > 
              <MenuItem value="">
                <em>All Centers</em>
              </MenuItem>
              {collectionCentersForFilter.map((center: any) => (
                <MenuItem key={center._id} value={center._id}>
                  {center.collectionCenterName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search"
            placeholder="Search by Name..."
            variant="outlined"
            value={searchInput}
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <DynamicTable
            headers={[
              "SN",
              "Name",
              "Phone Number",
              "Email",
              "Gender",
              "Specialization",
              "Category",
              "Collection Center",
              "Actions",
            ]}
            data={getPaginatedData()?.map((employee: any, index: number) => [
              page * rowsPerPage + index + 1,
              employee?.firstName || employee?.fullName || "N/A",
              employee?.phonNumber || employee?.phoneNumber || "N/A",
              employee?.email || "N/A",
              employee?.gender || "N/A",
              employee?.specialization || "N/A",
              employee?.categoryNameame || employee?.categoryName || "N/A",
              formatCollectionCenters(employee?.collectionCenter),
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={filteredEmployees.length}
            height={"60vh"}
            actionButtons={[
              {
                icon: (
                  <Tooltip
                    title="Allocate Collection Centers"
                    arrow
                    placement="top"
                  >
                    <BusinessIcon
                      sx={{
                        color: "#4CAF50",
                        cursor: "pointer",
                        "&:hover": { color: "#45a049" },
                      }}
                    />
                  </Tooltip>
                ),
                onClick: (rowIndex: number) => {
                  const paginatedEmployees = getPaginatedData();
                  const employee = paginatedEmployees[rowIndex];
                  handleAllocationClick(employee._id || employee.id);
                },
              },
            ]}
          />
        </Grid>
      </Grid>

      {/* Collection Center Allocation Dialog */}
      <Dialog
        open={openAllocationDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="600">
            Allocate Collection Centers
          </Typography>
          {(() => {
            const employee = employees.find(
              emp => (emp._id || emp.id) === selectedEmployeeId
            );
            const employeeName =
              employee?.firstName || employee?.fullName || "Employee";
            return (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Managing collection centers for: <strong>{employeeName}</strong>
              </Typography>
            );
          })()}
        </DialogTitle>
        <DialogContent>
          {loadingCenters ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: "auto" }}>
              {collectionCenters.map(center => {
                const isSelected = selectedCenterIds.includes(center._id);
                const employee = employees.find(
                  emp => (emp._id || emp.id) === selectedEmployeeId
                );
                const initiallyAllocated =
                  employee?.collectionCenter &&
                  Array.isArray(employee.collectionCenter)
                    ? employee.collectionCenter.some(
                        (c: any) => c._id === center._id
                      )
                    : false;

                return (
                  <ListItem
                    key={center._id}
                    sx={{
                      px: 0,
                      backgroundColor: initiallyAllocated
                        ? "#f0f8f0"
                        : "transparent",
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={e =>
                            handleCenterSelection(center._id, e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            variant="body1"
                            fontWeight="500"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {center.collectionCenterName}
                            {initiallyAllocated && (
                              <Typography
                                variant="caption"
                                sx={{
                                  backgroundColor: "#4caf50",
                                  color: "white",
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: "10px",
                                }}
                              >
                                Currently Assigned
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {center?.address}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%" }}
                    />
                  </ListItem>
                );
              })}
              {collectionCenters.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  py={2}
                >
                  No collection centers available
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={allocatingCenters}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAllocateCenters}
            variant="contained"
            // disabled={allocatingCenters || selectedCenterIds.length === 0}
            startIcon={
              allocatingCenters ? <CircularProgress size={16} /> : null
            }
          >
            {allocatingCenters ? "Allocating..." : "Allocate Centers"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Employee;
